import {
    type ChunkDataPool,
    type ChunkLayer,
    type ChunkSource,
    type LayerLayout,
    type ReadRequest
} from '@playcanvas/splat-transform';
import { Mat3, Mat4, Quat, Vec3 } from 'playcanvas';

import { ColorGrade, createGradeTerms, dcDecode, dcEncode } from './color-grade';
import { SHRotation } from './sh-utils';

// per-instance transform and grade lookups, keyed by instance index
interface TransformLookup {
    getMat(index: number): Mat4;
    getRot(index: number): Quat;
    getScale(index: number): Vec3;
    getSHRot(index: number): SHRotation;
}

interface GradeLookup {
    get(index: number): ColorGrade;
}

const shBandCoeffs = [0, 3, 8, 15];

// Number of f_rest_* SH coefficients per band level (mirrors splat-transform's
// SH_REST_COUNTS; that constant isn't exported from the package root).
const SH_REST_COUNTS: Record<number, number> = { 0: 0, 1: 9, 2: 24, 3: 45 };

// Build the canonical per-layer byte layout splat-transform expects. The
// interleaved packing here must match splat-transform's readers/materialize:
// position = xyz (stride 12); geometric = rot0-3, scale0-2, opacity (stride 32);
// color = dc0-2 then f_rest_* (stride (3 + numRest) * 4).
const buildLayouts = (numRest: number): Partial<Record<ChunkLayer, LayerLayout>> => ({
    position: {
        stride: 12,
        fields: { position: { byteOffset: 0, components: 3, type: 'float32' } }
    },
    geometric: {
        stride: 32,
        fields: {
            rotation: { byteOffset: 0, components: 4, type: 'float32' },
            scale: { byteOffset: 16, components: 3, type: 'float32' },
            opacity: { byteOffset: 28, components: 1, type: 'float32' }
        }
    },
    color: {
        stride: (3 + numRest) * 4,
        fields: numRest > 0 ? {
            dc: { byteOffset: 0, components: 3, type: 'float32' },
            shRest: { byteOffset: 12, components: numRest, type: 'float32' }
        } : {
            dc: { byteOffset: 0, components: 3, type: 'float32' }
        }
    }
});

// the same transform for every instance, e.g. an LCC2 environment
const fixedTransform = (mat: Mat4): TransformLookup => {
    const rot = new Quat().setFromMat4(mat);
    const scale = mat.getScale(new Vec3());
    const shRot = new SHRotation(new Mat3().setFromQuat(rot));
    return { getMat: () => mat, getRot: () => rot, getScale: () => scale, getSHRot: () => shRot };
};

const identityGrade = (): GradeLookup => {
    const grade = new ColorGrade(createGradeTerms());
    return { get: () => grade };
};

/**
 * Gather `rows` from `source` and write them transformed into the request
 * buffers at `outputOffset`. `instances[i]` keys the transform/grade lookups
 * for row i (two instances of one row can carry different transforms).
 */
const bakeGaussians = async (
    source: ChunkSource,
    rows: Uint32Array,
    instances: Uint32Array,
    transform: TransformLookup,
    gradeLookup: GradeLookup,
    pool: ChunkDataPool,
    request: ReadRequest,
    outputOffset: number,
    numRest: number
) => {
    const count = rows.length;
    const position = request.position ? pool.acquire('position', source.meta.layouts.position, count) : undefined;
    const geometric = request.geometric ? pool.acquire('geometric', source.meta.layouts.geometric, count) : undefined;
    const color = request.color ? pool.acquire('color', source.meta.layouts.color, count) : undefined;
    try {
        await source.read({ indices: rows, indexOffset: 0, count, position, geometric, color });
        const srcPosition = position ? new Float32Array(position.data) : null;
        const srcGeometric = geometric ? new Float32Array(geometric.data) : null;
        const srcColor = color ? new Float32Array(color.data) : null;
        const dstPosition = request.position ? new Float32Array(request.position.data) : null;
        const dstGeometric = request.geometric ? new Float32Array(request.geometric.data) : null;
        const dstColor = request.color ? new Float32Array(request.color.data) : null;
        const srcRest = SH_REST_COUNTS[source.meta.shBands];
        const srcCoeffs = shBandCoeffs[source.meta.shBands];
        const dstCoeffs = numRest / 3;
        const rest = new Float32Array(numRest);
        const tmpSH = new Float32Array(dstCoeffs);
        const positionValue = new Vec3();
        const rotationValue = new Quat();
        const c = { r: 0, g: 0, b: 0 };

        for (let i = 0; i < count; ++i) {
            const instance = instances[i];
            if (dstPosition) {
                const src = i * 3;
                const dst = (outputOffset + i) * 3;
                positionValue.set(srcPosition[src], srcPosition[src + 1], srcPosition[src + 2]);
                transform.getMat(instance).transformPoint(positionValue, positionValue);
                dstPosition[dst] = positionValue.x;
                dstPosition[dst + 1] = positionValue.y;
                dstPosition[dst + 2] = positionValue.z;
            }
            if (dstGeometric) {
                const src = i * 8;
                const dst = (outputOffset + i) * 8;
                rotationValue.set(srcGeometric[src + 1], srcGeometric[src + 2], srcGeometric[src + 3], srcGeometric[src]);
                rotationValue.mul2(transform.getRot(instance), rotationValue);
                dstGeometric[dst] = rotationValue.w;
                dstGeometric[dst + 1] = rotationValue.x;
                dstGeometric[dst + 2] = rotationValue.y;
                dstGeometric[dst + 3] = rotationValue.z;
                const scale = transform.getScale(instance);
                dstGeometric[dst + 4] = Math.log(Math.exp(srcGeometric[src + 4]) * scale.x);
                dstGeometric[dst + 5] = Math.log(Math.exp(srcGeometric[src + 5]) * scale.y);
                dstGeometric[dst + 6] = Math.log(Math.exp(srcGeometric[src + 6]) * scale.z);
                const grade = gradeLookup.get(instance);
                dstGeometric[dst + 7] = grade.hasTransparency ?
                    grade.applyOpacity(srcGeometric[src + 7]) : srcGeometric[src + 7];
            }
            if (dstColor) {
                const grade = gradeLookup.get(instance);
                const src = i * (3 + srcRest);
                const dst = (outputOffset + i) * (3 + numRest);
                c.r = dcDecode(srcColor[src]);
                c.g = dcDecode(srcColor[src + 1]);
                c.b = dcDecode(srcColor[src + 2]);
                if (grade.hasTint) grade.applyDC(c);
                dstColor[dst] = dcEncode(c.r);
                dstColor[dst + 1] = dcEncode(c.g);
                dstColor[dst + 2] = dcEncode(c.b);

                rest.fill(0);
                if (dstCoeffs > 0) {
                    for (let channel = 0; channel < 3; ++channel) {
                        tmpSH.fill(0);
                        const copy = Math.min(srcCoeffs, dstCoeffs);
                        for (let coeff = 0; coeff < copy; ++coeff) {
                            tmpSH[coeff] = srcColor[src + 3 + channel * srcCoeffs + coeff];
                        }
                        transform.getSHRot(instance).apply(tmpSH);
                        rest.set(tmpSH, channel * dstCoeffs);
                    }
                    if (grade.hasTint) {
                        for (let coeff = 0; coeff < dstCoeffs; ++coeff) {
                            c.r = rest[coeff];
                            c.g = rest[dstCoeffs + coeff];
                            c.b = rest[dstCoeffs * 2 + coeff];
                            grade.applySH(c);
                            rest[coeff] = c.r;
                            rest[dstCoeffs + coeff] = c.g;
                            rest[dstCoeffs * 2 + coeff] = c.b;
                        }
                    }
                    dstColor.set(rest, dst + 3);
                }
            }
        }
    } finally {
        position?.release();
        geometric?.release();
        color?.release();
    }
};

export {
    bakeGaussians,
    buildLayouts,
    fixedTransform,
    identityGrade,
    SH_REST_COUNTS,
    type GradeLookup,
    type TransformLookup
};
